import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { ArrowRight, Palette, Globe, Share2, TrendingUp, Bot, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import ServiceCard from "@/components/ServiceCard";
import TestimonialCard from "@/components/TestimonialCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  {
    title: "Design",
    description: "Graphic Design, Product Shots, 3D Design, Label & Packaging - everything visual for your brand.",
    icon: Palette,
    path: "/services/design",
    image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80",
    accentColor: "pink"
  },
  {
    title: "Web",
    description: "E-commerce sites, local business websites, SEO & hosting solutions that drive results.",
    icon: Globe,
    path: "/services/web",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
    accentColor: "blue"
  },
  {
    title: "Social Marketing",
    description: "TikTok content, short form videos, and UGC that connects with your audience.",
    icon: Share2,
    path: "/services/social-marketing",
    image: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=800&q=80",
    accentColor: "pink"
  },
  {
    title: "Digital Marketing",
    description: "Course creation, PDF courses, and marketing strategies to grow your business.",
    icon: TrendingUp,
    path: "/services/digital-marketing",
    image: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=800&q=80",
    accentColor: "blue"
  },
  {
    title: "AI Services",
    description: "Automation, AI-powered landing pages, and cutting-edge AI tools for efficiency.",
    icon: Bot,
    path: "/services/ai-services",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80",
    accentColor: "pink"
  },
  {
    title: "Learn Skills",
    description: "Online courses to help you master TikTok Shop, brand building, and digital business skills.",
    icon: GraduationCap,
    path: "/services/learn-skills",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80",
    accentColor: "blue"
  },
];

const stats = [
  { value: "78", label: "Projects Delivered" },
  { value: "98%", label: "Client Satisfaction" },
  { value: "30+", label: "Happy Clients" },
  { value: "15+", label: "Years Experience" },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <div data-testid="home-page" className="pt-24 md:pt-32 bg-black">
      <Helmet>
        <title>Barton | Digital Agency for Small Business Growth | Web, Social & AI</title>
        <meta name="description" content="Barton helps small businesses grow online with expert web design, social media marketing, digital advertising, and AI solutions. Get in touch for a free consultation." />
        <meta property="og:title" content="Barton | Digital Agency for Small Business Growth" />
        <meta property="og:description" content="We help small businesses grow online with expert web design, social media marketing, digital advertising, and AI solutions." />
        <meta property="og:url" content="https://barton.uk" />
        <meta property="og:image" content="https://barton.uk/og-image.png" />
        <meta name="twitter:title" content="Barton | Digital Agency for Small Business Growth" />
        <meta name="twitter:description" content="We help small businesses grow online with expert web design, social media marketing, digital advertising, and AI solutions." />
        <meta name="twitter:image" content="https://barton.uk/og-image.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 text-sm font-medium mb-6 border border-purple-500/20">
              Small Business Growth Partner
            </span>
            
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 text-white">
              We help small businesses{" "}
              <span className="text-gradient">grow online</span>
            </h1>
            
            <p className="text-white/60 text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
              From stunning designs to viral content, we provide everything your business needs to thrive in the digital world.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button
                asChild
                data-testid="hero-cta-button"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white rounded-full px-8 py-6 text-lg"
              >
                <Link to="/contact">
                  Get In Touch <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              
              <Button
                variant="outline"
                data-testid="hero-secondary-button"
                className="rounded-full px-8 py-6 text-lg border-white/20 text-white hover:bg-white/10 bg-transparent"
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Services
              </Button>
            </div>
          </div>

          <div className="relative animate-fade-in-up animation-delay-200">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                alt="Creative team working"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            </div>
            
            {/* Floating Stats Card */}
            <div className="absolute -bottom-6 -left-6 bg-[#111111] border border-white/10 rounded-xl shadow-xl p-4 animate-fade-in animation-delay-400">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                  <span className="text-white font-heading font-bold text-lg">98%</span>
                </div>
                <div>
                  <p className="font-semibold text-sm text-white">Client Satisfaction</p>
                  <p className="text-white/50 text-xs">Based on 30+ reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gradient-to-r from-purple-500/10 via-transparent to-pink-500/10 py-12 md:py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                data-testid={`stat-item-${index}`}
                className="text-center"
              >
                <div className={`font-heading text-4xl md:text-5xl font-bold mb-2 ${index % 2 === 0 ? "text-pink-400" : "text-purple-400"}`}>
                  {stat.value}
                </div>
                <div className="text-white/50 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500/10 text-purple-400 text-sm font-medium mb-4">
              Our Services
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">
              Everything you need to{" "}
              <span className="text-gradient">succeed online</span>
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              We offer a comprehensive suite of digital services tailored for small businesses ready to make their mark.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                title={service.title}
                description={service.description}
                icon={service.icon}
                path={service.path}
                image={service.image}
                accentColor={service.accentColor}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-[#0A0A0A] py-16 md:py-24">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500/10 text-pink-400 text-sm font-medium mb-4">
              Testimonials
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">
              What our clients say
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Don't just take our word for it. Here's what small business owners have to say about working with us.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((testimonial, index) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="relative rounded-3xl p-8 md:p-16 text-center text-white overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-transparent to-pink-500/20" />
            <div className="absolute inset-0 border border-white/10 rounded-3xl" />
            
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-r from-purple-500 to-pink-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-r from-blue-500 to-purple-500/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                Ready to grow your business?
              </h2>
              <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
                Let's discuss your project and create something amazing together. Get in touch today for a free consultation.
              </p>
              <Button
                asChild
                data-testid="cta-section-button"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full px-10 py-6 text-lg"
              >
                <Link to="/contact">
                  Get In Touch <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
